import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { refreshCalendar, type BlockReason, type CalendarBlock } from '../../lib/calendar';
import { useLocale, useT, type TKey } from '../../lib/i18n';
import { addDays, dateOf, formatDateShort, formatTime, hhmm, instantAt, minuteOfDay, todayIn } from '../../lib/time';
import { Select, Switch } from '../forms';
import { Button, ErrorNote, Field } from '../ui';
import type { Draft, StaffOption } from './AppointmentPanel';

const reasons: BlockReason[] = ['pause', 'day_off', 'sick', 'vacation', 'meeting', 'other'];
const messageOf = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : e ? fallback : null);

export type Affected = {
  id: string;
  startsAt: string;
  endsAt: string;
  version: number;
  staffMemberId: string;
  customerName: string | null;
  service: string;
};

/** "Tijd blokkeren": one person or the whole salon, a period, a reason, optionally repeating. */
export function BlockForm({
  orgId,
  locationId,
  tz,
  staff,
  role,
  ownStaffId,
  prefill,
  onDone,
  onDraft,
}: {
  orgId: string;
  locationId: string;
  tz: string;
  staff: StaffOption[];
  role: 'owner' | 'manager' | 'staff';
  ownStaffId: string | null;
  prefill: { date: string; minute?: number; minutes?: number; staffId: string | null };
  onDone: (affected: Affected[]) => void;
  onDraft?: (draft: Draft | null) => void;
}) {
  const t = useT();
  const qc = useQueryClient();
  const manager = role !== 'staff';
  const [who, setWho] = useState<string>(manager ? (prefill.staffId ?? '') : (ownStaffId ?? ''));
  const [date, setDate] = useState(prefill.date);
  const [allDay, setAllDay] = useState(false);
  const start = prefill.minute ?? 12 * 60;
  const [from, setFrom] = useState(hhmm(start));
  const [until, setUntil] = useState(hhmm(Math.min(start + (prefill.minutes ?? 30), 23 * 60 + 55)));
  const [reason, setReason] = useState<BlockReason>('pause');

  // Show the blocked time in the calendar while it's being set up.
  const fromMinute = allDay ? 0 : toMinute(from);
  const untilMinute = allDay ? 24 * 60 : toMinute(until);
  const reasonLabel = t(`reason.${reason}` as TKey);
  useEffect(() => {
    onDraft?.(who && untilMinute > fromMinute ? { date, minute: fromMinute, minutes: untilMinute - fromMinute, staffId: who, label: reasonLabel } : null);
  }, [onDraft, who, date, fromMinute, untilMinute, reasonLabel]);
  useEffect(() => () => onDraft?.(null), [onDraft]);
  const [repeat, setRepeat] = useState<'none' | 'daily' | 'weekly'>('none');
  const [repeatUntil, setRepeatUntil] = useState(addDays(prefill.date, 7 * 12));

  const add = useMutation({
    mutationFn: async (body: { startsAt: string; endsAt: string; allDay: boolean; reason: BlockReason; repeat: typeof repeat; repeatUntil?: string }) => {
      await api(`/orgs/${orgId}/blocks`, { method: 'POST', body: JSON.stringify({ locationId, staffMemberId: who || null, ...body }) });
      const q = new URLSearchParams({ locationId, from: body.startsAt, to: body.endsAt, ...(who ? { staffMemberId: who } : {}) });
      return (await api<{ appointments: Affected[] }>(`/orgs/${orgId}/affected?${q}`)).appointments;
    },
    onSuccess: (affected) => {
      refreshCalendar(qc, orgId);
      void qc.invalidateQueries({ queryKey: ['blocks'] });
      onDone(affected);
    },
  });

  function submit() {
    const startsAt = instantAt(date, allDay ? 0 : toMinute(from), tz);
    const endsAt = allDay ? instantAt(addDays(date, 1), 0, tz) : instantAt(date, toMinute(until), tz);
    add.mutate({ startsAt, endsAt, allDay, reason, repeat, repeatUntil: repeat === 'none' ? undefined : repeatUntil });
  }

  function sickToday() {
    const now = Date.now();
    const today = todayIn(tz);
    setReason('sick');
    add.mutate({
      startsAt: instantAt(today, Math.floor(minuteOfDay(now, tz) / 5) * 5, tz),
      endsAt: instantAt(addDays(today, 1), 0, tz),
      allDay: false,
      reason: 'sick',
      repeat: 'none',
    });
  }

  return (
    <div className="flex flex-col gap-4">
      <Select id="blk-who" label={t('block.who')} value={who} onChange={(e) => setWho(e.target.value)} disabled={!manager}>
        {manager && <option value="">{t('block.wholeSalon')}</option>}
        {staff
          .filter((s) => manager || s.id === ownStaffId)
          .map((s) => (
            <option key={s.id} value={s.id}>
              {s.displayName}
            </option>
          ))}
      </Select>
      {who && (
        <Button type="button" variant="secondary" className="self-start" onClick={sickToday} disabled={add.isPending}>
          {t('block.sickToday')}
        </Button>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field id="blk-date" type="date" label={t('block.date')} value={date} onChange={(e) => setDate(e.target.value)} />
        <Select id="blk-reason" label={t('block.reason')} value={reason} onChange={(e) => setReason(e.target.value as BlockReason)}>
          {reasons.map((r) => (
            <option key={r} value={r}>
              {t(`reason.${r}` as TKey)}
            </option>
          ))}
        </Select>
      </div>
      <Switch label={t('block.allDay')} checked={allDay} onChange={setAllDay} />
      {!allDay && (
        <div className="grid grid-cols-2 gap-3">
          <Field id="blk-from" type="time" step={300} label={t('block.from')} value={from} onChange={(e) => setFrom(e.target.value)} />
          <Field id="blk-until" type="time" step={300} label={t('block.until')} value={until} onChange={(e) => setUntil(e.target.value)} />
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Select id="blk-repeat" label={t('block.repeat')} value={repeat} onChange={(e) => setRepeat(e.target.value as typeof repeat)}>
          <option value="none">{t('block.repeatNone')}</option>
          <option value="daily">{t('block.repeatDaily')}</option>
          <option value="weekly">{t('block.repeatWeekly')}</option>
        </Select>
        {repeat !== 'none' && <Field id="blk-repeat-until" type="date" label={t('block.repeatUntil')} value={repeatUntil} onChange={(e) => setRepeatUntil(e.target.value)} />}
      </div>
      <ErrorNote message={messageOf(add.error, t('error.generic'))} />
      <Button type="button" onClick={submit} disabled={add.isPending}>
        {t('block.add')}
      </Button>
    </div>
  );
}

const toMinute = (v: string) => {
  const [h, m] = v.split(':').map(Number) as [number, number];
  return h * 60 + m;
};

/** Details of a blocked time, with delete. */
export function BlockView({ orgId, block, tz, staff, canDelete, onDone }: { orgId: string; block: CalendarBlock; tz: string; staff: StaffOption[]; canDelete: boolean; onDone: () => void }) {
  const t = useT();
  const locale = useLocale();
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: () => api(`/orgs/${orgId}/blocks/${block.blockId}`, { method: 'DELETE' }),
    onSuccess: () => {
      refreshCalendar(qc, orgId);
      void qc.invalidateQueries({ queryKey: ['blocks'] });
      onDone();
    },
  });
  const who = block.staffMemberId ? staff.find((s) => s.id === block.staffMemberId)?.displayName : t('block.wholeSalon');
  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="text-lg font-semibold">
          {t(`reason.${block.reason}` as TKey)} · {who}
        </p>
        <p className="tabular-nums text-muted">
          {formatDateShort(block.date, locale)}
          {block.allDay ? ` · ${t('block.allDay')}` : ` · ${formatTime(block.start, tz)}–${formatTime(block.end, tz)}`}
        </p>
        {block.repeats && <p className="text-sm text-muted">{t('block.repeat')} ✓</p>}
        {block.note && <p className="mt-2 text-sm">{block.note}</p>}
      </div>
      <ErrorNote message={messageOf(remove.error, t('error.generic'))} />
      {canDelete && (
        <Button variant="secondary" className="self-start text-danger" onClick={() => remove.mutate()} disabled={remove.isPending}>
          {t('common.delete')}
        </Button>
      )}
    </div>
  );
}

/** After blocking time: appointments that clash, each with a suggested colleague. */
export function AffectedAppointments({ orgId, tz, staff, appointments, onDone }: { orgId: string; tz: string; staff: StaffOption[]; appointments: Affected[]; onDone: () => void }) {
  const t = useT();
  const [handled, setHandled] = useState<Record<string, string>>({});
  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="font-semibold">{t('affected.title')}</p>
        <p className="text-sm text-muted">{t('affected.intro')}</p>
      </div>
      <ul className="flex flex-col gap-3">
        {appointments.map((a) => (
          <AffectedRow key={a.id} orgId={orgId} tz={tz} staff={staff} appointment={a} handled={handled[a.id]} onHandled={(label) => setHandled((h) => ({ ...h, [a.id]: label }))} />
        ))}
      </ul>
      <Button onClick={onDone}>{t('affected.done')}</Button>
    </div>
  );
}

function AffectedRow({ orgId, tz, staff, appointment: a, handled, onHandled }: { orgId: string; tz: string; staff: StaffOption[]; appointment: Affected; handled?: string; onHandled: (label: string) => void }) {
  const t = useT();
  const locale = useLocale();
  const qc = useQueryClient();
  const colleagues = useQuery({
    queryKey: ['colleagues', orgId, a.id],
    queryFn: () => api<{ staffIds: string[] }>(`/orgs/${orgId}/appointments/${a.id}/colleagues`).then((r) => r.staffIds),
  });
  const best = colleagues.data?.[0];
  const bestName = staff.find((s) => s.id === best)?.displayName;
  const move = useMutation({
    mutationFn: () => api(`/orgs/${orgId}/appointments/${a.id}/move`, { method: 'POST', body: JSON.stringify({ startsAt: a.startsAt, staffMemberId: best, version: a.version }) }),
    onSuccess: () => {
      refreshCalendar(qc, orgId, a.id);
      onHandled(t('affected.moved', { name: bestName ?? '' }));
    },
  });
  return (
    <li className="flex flex-col gap-2 rounded-[var(--radius-control)] border border-line bg-surface p-3">
      <p>
        <span className="font-semibold tabular-nums">
          {formatDateShort(dateOf(a.startsAt, tz), locale)} {formatTime(a.startsAt, tz)}
        </span>{' '}
        {a.customerName ?? t('cal.noClient')} <span className="text-muted">· {a.service}</span>
      </p>
      {handled ? (
        <p className="text-sm font-medium text-accent-strong">✓ {handled}</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {colleagues.isPending ? null : best ? (
            <Button variant="secondary" onClick={() => move.mutate()} disabled={move.isPending}>
              {t('affected.moveTo', { name: bestName ?? '' })}
            </Button>
          ) : (
            <span className="self-center text-sm text-muted">{t('affected.noColleague')}</span>
          )}
          <Button variant="ghost" onClick={() => onHandled(t('affected.leave'))}>
            {t('affected.leave')}
          </Button>
        </div>
      )}
      <ErrorNote message={messageOf(move.error, t('error.generic'))} />
    </li>
  );
}
